import { useEffect, useState } from "react";
import { useMainContext, useMainDispatchContext } from "../../MainContext";
import { ReactComponent as ArrowUp } from "../../assets/arrow-up-circle.svg";
import { ReactComponent as PlayIcon } from "../../assets/play-circle.svg";
import {
  formatTime,
  getByFieldValue,
  getTimezoneFromUser,
  isEmptyList,
  isNullOrUndefined,
} from "../../utils";
import "./styles.css";
import {
  getTurnaroundsOperationEventSearch,
  getTurnaroundsSummarySearch,
  getUsers,
} from "../../api";
import { getTurnaroundInfoForTurnaroundSummary } from "../../turnaroundUtils";
import WelcomeWidget from "../WelcomeWidget";
import { useTranslation } from "react-i18next";
import { POLLING_INTERVALS } from "../../constants";
import {
  useLiveFeedContext,
  useLiveFeedDispatchContext,
} from "./LiveFeedContext";
import moment from "moment-timezone";
import { getUserInfo } from "../../userUtils";
import LoadingIndicator from "../LoadingIndicator";
import ImageAssetModal from "../ImageAsset/ImageAssetModal";
import LiveFeedPost from "./LiveFeedPost";
import PhotoGrid from "./PhotoGrid";

function LiveFeed() {
  const { t } = useTranslation();
  const mainContext = useMainContext();
  const dispatch = useMainDispatchContext();
  const liveFeedContext = useLiveFeedContext();
  const liveFeedDispatch = useLiveFeedDispatchContext();
  const {
    currentUser,
    turnaroundsSummarySearch,
    turnaroundsOperationEventSearch,
    users,
  } = mainContext;
  const { liveFeedItems, lastUpdatedAt } = liveFeedContext;
  const timezone = getTimezoneFromUser(currentUser);
  const [updateLiveFeedRequested, setUpdateLiveFeedRequested] = useState(false);
  const [imageAssetToShow, setImageAssetToShow] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [includeNotes, setIncludeNotes] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    async function initialize() {
      // Load supporting data
      await getUsers(dispatch);

      // Load turnarounds
      const mNow = moment().tz(timezone);
      const turnsStart = moment(mNow).add(-4, "hour").toDate().toISOString();
      const turnsEnd = moment(mNow).add(4, "hour").toDate().toISOString();
      await getTurnaroundsSummarySearch(dispatch, {
        startTime: turnsStart,
        endTime: turnsEnd,
        includeOpen: true,
        includeClosed: true,
      });

      // Load initial batch of live feed items
      const eventsStart = moment(mNow).add(-4, "hour").toDate().toISOString();
      const eventsEnd = mNow.toDate().toISOString();
      await getTurnaroundsOperationEventSearch(
        dispatch,
        eventsStart,
        eventsEnd
      );
      setIsLoading(false);
    }
    initialize();
  }, [dispatch, timezone]);

  useEffect(() => {
    // Handle loaded events
    if (
      !isEmptyList(turnaroundsSummarySearch?.turnarounds) &&
      !isEmptyList(turnaroundsOperationEventSearch) &&
      !isEmptyList(users)
    ) {
      const liveFeedItemsToAdd = [];
      for (let i = 0; i < turnaroundsOperationEventSearch.length; i++) {
        const item = turnaroundsOperationEventSearch[i];
        const turnaround = getByFieldValue(
          turnaroundsSummarySearch.turnarounds,
          "uuid",
          item.turnaroundUuid
        );
        const user = getByFieldValue(users, "uuid", item.submittedByUserUuid);
        const userInfo = !isNullOrUndefined(user)
          ? getUserInfo(user)
          : {
              user: {
                uuid: item.submittedByUserUuid,
                firstName: t("system"),
                lastName: "",
              },
              uuid: item.submittedByUserUuid,
              fullName: t("system"),
            }; // Placeholder system user
        if (!isNullOrUndefined(turnaround) && !isNullOrUndefined(userInfo)) {
          liveFeedItemsToAdd.push({
            userInfo: userInfo,
            turnaroundInfo: getTurnaroundInfoForTurnaroundSummary(turnaround),
            ...item,
          });
        }
      }

      liveFeedDispatch({
        type: "addLiveFeedItems",
        value: liveFeedItemsToAdd,
      });
    }
  }, [
    t,
    liveFeedDispatch,
    turnaroundsSummarySearch,
    users,
    turnaroundsOperationEventSearch,
  ]);

  useEffect(() => {
    // Handle polling for additional data
    if (updateLiveFeedRequested && !isNullOrUndefined(lastUpdatedAt)) {
      const mNow = moment().tz(timezone);

      // Load more turnarounds
      const turnsStart = moment(mNow).add(-4, "hour").toDate().toISOString();
      const turnsEnd = moment(mNow).add(4, "hour").toDate().toISOString();
      getTurnaroundsSummarySearch(dispatch, {
        startTime: turnsStart,
        endTime: turnsEnd,
        includeOpen: true,
        includeClosed: true,
      });

      // Load more live feed items
      const end = mNow.toDate().toISOString();
      getTurnaroundsOperationEventSearch(
        dispatch,
        lastUpdatedAt.toISOString(),
        end
      );
      setUpdateLiveFeedRequested(false);
    }
  }, [dispatch, timezone, updateLiveFeedRequested, lastUpdatedAt]);

  useEffect(() => {
    // Setup polling intervals
    const intervals = [];
    if (POLLING_INTERVALS.LIVE_FEED > 0) {
      const interval = setInterval(() => {
        setUpdateLiveFeedRequested(true);
      }, POLLING_INTERVALS.LIVE_FEED);
      intervals.push(interval);
    }
    return () => {
      for (let i = 0; i < intervals.length; i++) {
        clearInterval(intervals[i]);
      }
    };
  }, []);

  useEffect(() => {
    // Scroll listeners
    function handleScroll(e) {
      setIsScrolled(e.target.scrollTop > 0);
    }
    const mainContentEl = document.querySelector(".main-content-box");
    mainContentEl?.addEventListener("scroll", handleScroll);
    return () => {
      mainContentEl?.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const liveFeedItemsToRender = !isEmptyList(liveFeedItems)
    ? liveFeedItems.filter((feedItem) => {
        return !isNullOrUndefined(feedItem.image) || includeNotes;
      })
    : [];

  function handleShowImageAsset(imageAsset, autoplay) {
    const imageAssetToShow = {
      ...imageAsset.image,
      caption: (
        <div className="caption-stacked">
          <div>{imageAsset.eventType?.name}</div>
          <div>{imageAsset.operationType?.name}</div>
        </div>
      ),
      notes: imageAsset.notes,
      uuid: imageAsset.uuid,
      autoplay: autoplay,
    };
    setImageAssetToShow(imageAssetToShow);
  }

  function handleImageAssetNavigation(increment) {
    const currIdx = liveFeedItemsToRender.findIndex((i) => {
      return imageAssetToShow?.uuid === i.uuid;
    });
    let nextIdx = 0;
    if (
      currIdx === -1 ||
      currIdx + increment > liveFeedItemsToRender.length - 1
    ) {
      // take first
      nextIdx = 0;
    } else if (currIdx + increment < 0) {
      // take last
      nextIdx = liveFeedItemsToRender.length - 1;
    } else {
      nextIdx = currIdx + increment;
    }
    if (nextIdx !== currIdx) {
      handleShowImageAsset(liveFeedItemsToRender[nextIdx]);
    }
  }

  return (
    <div className="live-feed">
      <div className="live-feed-main">
        <div className="live-feed-posts">
          <div className={`live-feed-scroll${isScrolled ? " enabled" : ""}`}>
            <ArrowUp
              onClick={() => {
                document.querySelector(".main-content-box").scrollTo({
                  top: 0,
                  left: 0,
                });
              }}
            />
          </div>
          <div>
            <div className="live-feed-header">
              <div>
                <h4>{t("recent_activity")}</h4>
              </div>
              <div className="live-feed-header-info">
                <div>
                  {lastUpdatedAt && (
                    <>{`${t("last_updated")} ${formatTime(
                      lastUpdatedAt,
                      timezone
                    )}`}</>
                  )}
                </div>
                <div>
                  {!isEmptyList(liveFeedItemsToRender) && (
                    <div className="autoplay-button">
                      <PlayIcon
                        onClick={() => {
                          handleShowImageAsset(liveFeedItemsToRender[0], true);
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
            <hr />
          </div>
          {!isEmptyList(liveFeedItemsToRender) &&
            liveFeedItemsToRender.map((i, idx) => (
              <LiveFeedPost
                key={idx}
                feedItem={i}
                timezone={timezone}
                onClick={handleShowImageAsset}
              />
            ))}
          {!isLoading && isEmptyList(liveFeedItemsToRender) && (
            <div className="empty-state fadeIn">
              <div>{t("no_recent_activity")}</div>
            </div>
          )}
          {isLoading && (
            <div>
              <LoadingIndicator />
            </div>
          )}
          <div>
            {!isNullOrUndefined(imageAssetToShow) && (
              <ImageAssetModal
                imageAsset={imageAssetToShow}
                onClose={() => {
                  setImageAssetToShow(null);
                }}
                onNext={() => {
                  handleImageAssetNavigation(1);
                }}
                onPrev={() => {
                  handleImageAssetNavigation(-1);
                }}
                showNavigation={liveFeedItemsToRender.length > 1}
              />
            )}
          </div>
        </div>
      </div>
      <div className="live-feed-sidepanel">
        <WelcomeWidget />
        <PhotoGrid
          feedItems={liveFeedItemsToRender}
          includeNotes={includeNotes}
          onToggleIncludeNotes={() => {
            setIncludeNotes((prev) => !prev);
          }}
          onClick={handleShowImageAsset}
        />
      </div>
    </div>
  );
}

export default LiveFeed;
